import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface BaseProps {
  label: string
  name: string
  value?: string
  noFirstOption?: boolean
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  errors?: { [key: string]: string }
  firstOptionText?: string
}

interface MapOptionsProps extends BaseProps {
  options: Record<string, string>
}

interface ObjectOptionsProps<T> extends BaseProps {
  options: T[]
  keyProp: keyof T
  valueProp: keyof T
}

type Props<T> = MapOptionsProps | ObjectOptionsProps<T>

export const SelectField = <T,>(props: Props<T>) => {
  const {
    label,
    name,
    value,
    noFirstOption,
    handleInputChange,
    errors,
    firstOptionText = '-- Selecciona una opción --',
  } = props

  const [inputClass, setInputClass] = useState<string>('')
  const { t } = useTranslation()

  const inputClassDef = 'border rounded p-2'
  useEffect(() => {
    const hayError = errors?.[name]
    const borderColor = hayError ? 'ring-4 ring-red-500' : ''
    setInputClass(`${inputClassDef} ${borderColor}`)
  }, [errors, name])

  const handleInputChangeInt = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    const hayError = errors?.[name]

    if (hayError && value !== '') {
      setInputClass(inputClassDef)
    }

    handleInputChange(e)
  }

  return (
    <div className="flex flex-col">
      <label className="font-semibold mb-1">{label}</label>
      <select className={inputClass} name={name} value={value?.trim()} onChange={handleInputChangeInt}>
        {!noFirstOption && <option value="">{firstOptionText}</option>}
        {Array.isArray(props.options) && 'keyProp' in props && 'valueProp' in props
          ? props.options.map((option) => (
              <option
                key={String(option[props.keyProp as keyof typeof option]).trim()}
                value={String(option[props.keyProp as keyof typeof option]).trim()}
              >
                {t(String(option[props.valueProp as keyof typeof option]).trim())}
              </option>
            ))
          : Object.entries(props.options).map(([key, display]) => (
              <option key={key.trim()} value={key.trim()}>
                {t(display)}
              </option>
            ))}
      </select>
    </div>
  )
}
